import ScrollToTopButton from "components/ScrollToTopButton";
import { OrderDetailsShimmer } from "components/shimmer/OrderDetailsShimmer";
import useDistrictData from "hook/useDistrictData";
import useFreeAmountDelivery from "hook/useFreeAmountDelivery";
import useStoreOrder from "hook/useStoreOrder";
import useUpazillaData from "hook/useUpzillaData";
import { useEffect, useRef, useState } from "react";
import { BsCartXFill } from "react-icons/bs";
import { FaStore, FaTruck, FaTruckPickup } from "react-icons/fa";
import { HiOutlineTrash, HiOutlineX } from "react-icons/hi";
import { TbCurrencyTaka } from "react-icons/tb";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSettings } from "context/SettingsContext";
import { t } from "context/translations";
import { clearCartItems, getCartItems, setCartItems } from "utils/cartStorage";
import { assetUrl } from "utils/api";

const OrderDetails = () => {
  const { freeAmountDeliveryData } = useFreeAmountDelivery();
  const freeDeliveryAmount = freeAmountDeliveryData?.[0]?.amount || 0;
  const { placeOrder, cancelOrder } = useStoreOrder();
  const { theme, language } = useSettings();
  const isDark = theme === "dark";

  const deliveryOptions = [
    { label: t("insideDhaka", language), price: 70, icon: <FaTruck /> },
    { label: t("outsideDhaka", language), price: 130, icon: <FaTruckPickup /> },
    { label: t("pickupFromStore", language), price: 0, icon: <FaStore /> },
  ];
  const [form, setForm] = useState({
    name: "",
    phone: "",
    whatsappPhone: "",
    address: "",
    district: "",
    upazilla: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [isCheckoutSelection, setIsCheckoutSelection] = useState(false);
  const [selectedOption, setSelectedOption] = useState(deliveryOptions[0]);
  const { district, loadingDistrict, errorDistrict } = useDistrictData();
  const { upazilla } = useUpazillaData(form.district);
  const navigate = useNavigate();
  const hasFiredCheckout = useRef(false);
  // const purchaseFiredRef = useRef(false);
  // const [submitting, setSubmitting] = useState(false);

  const getCartItemKey = (item, index) =>
    item.cartKey ||
    [
      item.item_id,
      item.selectedVariant?.variant_id,
      item.selectedColor,
      item.selectedSize,
    ]
      .filter((value) => value !== undefined && value !== null)
      .join("_") ||
    String(index);

  const updateStoredProducts = (items) => {
    if (!isCheckoutSelection) {
      setCartItems(items);
      return;
    }

    const itemMap = new Map(
      items.map((item, index) => [getCartItemKey(item, index), item]),
    );
    const cartItems = getCartItems();
    const updatedCartItems = cartItems.map((item, index) => {
      const key = getCartItemKey(item, index);
      return itemMap.get(key) || item;
    });

    setCartItems(updatedCartItems);
    localStorage.setItem("checkoutItems", JSON.stringify(items));
  };

  const removeOrderedProducts = () => {
    if (!isCheckoutSelection) {
      clearCartItems();
      localStorage.removeItem("checkoutItems");
      return;
    }

    const orderedKeys = new Set(
      products.map((item, index) => getCartItemKey(item, index)),
    );
    const cartItems = getCartItems();
    const remainingItems = cartItems.filter(
      (item, index) => !orderedKeys.has(getCartItemKey(item, index)),
    );

    setCartItems(remainingItems);
    localStorage.removeItem("checkoutItems");
  };

  useEffect(() => {
    if (products.length > 0 && !hasFiredCheckout.current) {
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: "begin_checkout",
        ecommerce: {
          currency: "BDT",
          value: subtotal,
          items: products.map((item) => ({
            item_id: item.item_id,
            item_name: item.selectedVariant?.title,
            price: item.selectedVariant?.flashPrice,
            quantity: item.quantity || 1,
          })),
        },
      });
      hasFiredCheckout.current = true;
    }
  }, [products]);

  const hasVariantFreeDelivery = products.some(
    (item) => Number(item?.selectedVariant?.freeVariantDelivery) === 1,
  );

  const increaseQuantity = (index) => {
    const updated = [...products];
    updated[index].quantity = (updated[index].quantity || 1) + 1;
    setProducts(updated);
    updateStoredProducts(updated);
  };

  const decreaseQuantity = (index) => {
    const updated = [...products];
    if ((updated[index].quantity || 1) > 1) {
      updated[index].quantity -= 1;
      setProducts(updated);
      updateStoredProducts(updated);
    }
  };

  console.log("hasVariantFreeDelivery:", hasVariantFreeDelivery);

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const checkoutItems = JSON.parse(
        localStorage.getItem("checkoutItems") || "[]",
      );
      const storedItems = getCartItems();
      setProducts(checkoutItems.length > 0 ? checkoutItems : storedItems);
      setIsCheckoutSelection(checkoutItems.length > 0);
      setLoading(false);
    }, 500);
  }, []);

  const handleChange = (option) => {
    setSelectedOption(option);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "district") {
      setForm({ ...form, district: value, upazilla: "" });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "আপনার নাম লিখুন";
    if (!form.phone.trim()) newErrors.phone = "আপনার নাম্বার লিখুন";
    if (!/^01\d{9}$/.test(form.phone))
      newErrors.phone = "নাম্বার ০১ দিয়ে শুরু হতে হবে এবং ১১ ডিজিটের হতে হবে";
    if (form.whatsappPhone.trim() && !/^01\d{9}$/.test(form.whatsappPhone)) {
      newErrors.whatsappPhone =
        "নাম্বার ০১ দিয়ে শুরু হতে হবে এবং ১১ ডিজিটের হতে হবে";
    }

    if (!form.address.trim()) newErrors.address = "সম্পূর্ন ঠিকানা লিখুন";
    if (!form.district) newErrors.district = "জেলা নির্বাচন করুন";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const removeItem = (index) => {
    const removedKey = getCartItemKey(products[index], index);
    const updated = [...products];
    updated.splice(index, 1); // remove the item
    setProducts(updated);

    if (!isCheckoutSelection) {
      setCartItems(updated);
      return;
    }

    const cartItems = getCartItems();
    const remainingCartItems = cartItems.filter(
      (item, itemIndex) => getCartItemKey(item, itemIndex) !== removedKey,
    );

    setCartItems(remainingCartItems);
    localStorage.setItem("checkoutItems", JSON.stringify(updated));
  };

  const subtotal = products.reduce(
    (total, item) =>
      total + (item.selectedVariant?.flashPrice || 0) * (item.quantity || 1),
    0,
  );

  const isFreeDelivery =
    hasVariantFreeDelivery ||
    (freeDeliveryAmount > 0 && subtotal >= freeDeliveryAmount);

  const finalDeliveryPrice = isFreeDelivery ? 0 : selectedOption.price;
  const finalDeliveryLabel = isFreeDelivery
    ? "Free Delivery"
    : selectedOption.label;
  const totalPrice = subtotal + finalDeliveryPrice;

  const buildPayload = () => {
    const selectedDistrict = district?.data?.find(
      (d) => String(d.id) === String(form.district),
    );

    const selectedUpazilla = upazilla?.data?.find(
      (u) => u.bn_name === form.upazilla,
    );

    return {
      deliveryCharge: finalDeliveryLabel,
      name: form.name,
      phone: form.phone,
      whatsappPhone: form.whatsappPhone,
      address: form.address,
      district: selectedDistrict?.bn_name || "",
      upazilla: selectedUpazilla?.bn_name || "test",
      items: products.map((p) => ({
        id: Number(p.item_id),
        variant_id: Number(p.selectedVariant?.variant_id),
        color: String(p.selectedColor),
        size: String(p.selectedSize),
        quantity: Number(p.quantity || 1),
        price: String(p.selectedPrice),
      })),
    };
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const payload = buildPayload();
      await placeOrder(payload, totalPrice, products);

      removeOrderedProducts();
      setProducts([]);
      setForm({
        name: "",
        phone: "",
        whatsappPhone: "",
        address: "",
        district: "",
        upazilla: "",
      });

      navigate("/confirmOrder");
    } catch (error) {
      toast.error("❌ অর্ডার ব্যর্থ হয়েছে।");
    }
  };

  return (
    <div className="flex flex-col min-h-screen pb-52 md:pb-0 mt-1">
      <div className="flex-1 mb-3 relative md:max-w-8xl sm:px-4 2xl:px-0 lg:mx-16  md:pb-4 pb-0">
        {loading ? (
          <OrderDetailsShimmer />
        ) : products.length === 0 ? (
          <div className={`text-center ${isDark?"text-white":"text-gray-800"} py-10 my-5`}>
            <BsCartXFill className="mx-auto w-24 h-24 sm:w-40 sm:h-40 mb-5 opacity-60" />
            <h3 className="text-base sm:text-lg font-semibold mb-2 mt-5">
              {t("emptyCart", language)}
            </h3>
          </div>
        ) : (
          <>
            <div className="flex flex-col lg:flex-row gap-3">
              <div className={`w-full lg:w-2/3 mb-2 px-4 pt-2 ${isDark ? "bg-gray-100" : "bg-white"}`} >
                <div className=" rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border border-gray-200 px-4 py-2 shadow-sm">
                    <div className="font-bold text-base md:text-lg bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent">
                      {t("Checkout", language)}
                    </div>
                  </div>{" "}
                  <div className="p-3">
                    <div className="flex gap-6 mb-3">
                      <div className="w-full">
                        <p className="text-sm mb-1">
                          {t("name", language)}{" "}
                          <span className="text-red-500">*</span>
                        </p>
                        {/* <div className="flex items-center border rounded h-10 shadow-sm focus-within:ring-1 focus-within:ring-darkBgColor"> */}
                        <input
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleInputChange}
                          placeholder=""
                          className={`w-full h-10 px-3 rounded-md border border-gray-200 ${isDark ? "bg-gray-100" : "bg-gray-50 focus:border-red-400 focus:ring-2 focus:ring-red-100 focus:bg-white"}  outline-none transition-all duration-200`}
                        />
                        {/* </div> */}
                        {errors.name && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.name}
                          </p>
                        )}
                      </div>
                      <div className="w-full">
                        <p className="text-sm  mb-1">
                          {t("phone", language)}{" "}
                          <span className="text-red-500">*</span>
                        </p>
                        <input
                          type="tel"
                          placeholder=""
                          name="phone"
                          value={form.phone}
                          onChange={handleInputChange}
                          className={`w-full h-10 px-3 rounded-md border border-gray-200 
                            ${isDark ? "bg-gray-100" : "bg-gray-50 focus:border-red-400 focus:ring-2 focus:ring-red-100 focus:bg-white"} 
                            outline-none transition-all duration-200`}
                        />
                        {errors.phone && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.phone}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mb-3">
                      <p className="text-sm  mb-1">
                        {t("address", language)}{" "}
                        <span className="text-red-500">*</span>
                      </p>
                      <textarea
                        type="text"
                        placeholder=""
                        name="address"
                        value={form.address}
                        onChange={handleInputChange}
                        className={`mt-1 w-full h-24 px-3 py-2 rounded-md border border-gray-200 
                          ${isDark ? "bg-gray-100" : "bg-gray-50 focus:border-red-400 focus:ring-2 focus:ring-red-100 focus:bg-white"}
                          outline-none transition-all duration-200 resize-none`}
                      />
                      {errors.address && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.address}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-6 mb-3">
                      <div className="w-full">
                        <p className="text-sm  mb-1">
                          {t("district", language)}{" "}
                          <span className="text-red-500">*</span>
                        </p>
                        <select
                          name="district"
                          value={form.district}
                          onChange={handleInputChange}
                          className={`w-full h-10 px-3 rounded-md border border-gray-200 
                              ${isDark ? "bg-gray-100" : "bg-gray-50 focus:border-red-400 focus:ring-2 focus:ring-red-100 focus:bg-white"}
                              outline-none transition-all duration-200`}
                        >
                          <option value=""></option>
                          {loadingDistrict && <option>Loading...</option>}
                          {errorDistrict && (
                            <option>Error loading districts</option>
                          )}
                          {district?.data?.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.bn_name}
                            </option>
                          ))}
                        </select>
                        {errors.district && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.district}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="w-full">
                      <p className="text-sm mb-1">
                        {t("whatsappOrderForOther", language)}
                      </p>
                      <input
                        type="tel"
                        placeholder=""
                        name="whatsappPhone"
                        value={form.whatsappPhone}
                        onChange={handleInputChange}
                        className={`w-full h-10 px-3 rounded-md border border-gray-200 ${isDark ? "bg-gray-100" : "bg-gray-50 focus:border-red-400 focus:ring-2 focus:ring-red-100 focus:bg-white"} outline-none transition-all duration-200`}
                      />
                      {errors.whatsappPhone && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.whatsappPhone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full lg:w-1/3 mb-3 px-4 md:mt-2">
                <div className=" rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border border-gray-200 px-4 py-2 mb-3 shadow-sm">
                    <div className="font-bold text-base md:text-lg bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent">
                      {t("deliveryOptions", language)}
                    </div>
                  </div>{" "}
                  <div className="space-y-2 px-3 pb-2">
                    {deliveryOptions.map((option, index) => (
                      <label
                        key={index}
                        className="flex justify-between items-center cursor-pointer text-sm"
                      >
                        <div className="flex items-center gap-2">
                          {/* Radio */}
                          <input
                            type="radio"
                            name="deliveryOption"
                            checked={selectedOption.label === option.label}
                            onChange={() => handleChange(option)}
                            className=" text-darkBgColor cursor-pointer"
                          />

                          {/* Icon + Label */}
                          {/* <span className="text-darkBgColor text-base">
                            {option.icon}
                          </span> */}
                          <span lang="bn" className="text-base">
                            {option.label}
                          </span>
                        </div>

                        {/* Price */}
                        <span
                          className={`${isDark ? "text-white" : "text-backgroundColor"} font-medium`}
                        >
                          {option.price} {t("TK", language)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="mt-4 bg-white rounded sm:block hidden">
                  {" "}
                  <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border border-gray-200 mb-2">
                    <h2
                      className={`py-2 text-center  ${isDark ? "bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent" : " bg-lightBgColor"} rounded`}
                    >
                      {t("orderDetail", language)} <br />
                      <span className={`text-[10px] text-gray-600`}>
                        {t("moneyAfterReceiveProduct", language)}
                      </span>
                    </h2>
                  </div>
                  <div className="space-y-2 px-5">
                    <div className="flex justify-between text-sm">
                      <span lang="bn" className="text-base">
                        {t("price", language)} ({products.length} items)
                      </span>
                      <span className="text-backgroundColor">
                        {subtotal} {t("TK", language)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span lang="bn" className="text-base">
                        {t("deliveryCharge", language)}
                      </span>
                      <span className="text-backgroundColor">
                        {finalDeliveryPrice} {t("TK", language)}
                      </span>
                    </div>
                    <div className="font-semibold text-blue-600 border-dashed border-t border-darkBgColor pt-1 flex justify-between text-md">
                      <span lang="bn" className="text-base">
                        {t("subTotal", language)}
                      </span>
                      <span className="text-blue-600 font-semibold">
                        {totalPrice} {t("TK", language)}
                      </span>
                    </div>
                  </div>
                  <div className="mx-5">
                    <button
                      lang="bn"
                      onClick={handleSubmit}
                      className="w-full bg-darkBgColor text-white md:py-3  py-2 rounded my-4 transition-colors lg:text-base text-sm"
                    >
                      <span className="text-white">🛒</span>{" "}
                      {t("order", language)}
                    </button>
                  </div>
                </div>
              </div>

              {freeDeliveryAmount > 0 ? (
                <div className="mb-2">
                  <p className="flex items-center text-backgroundColor font-bold text-center">
                    🎁🎁 {freeDeliveryAmount} {t("freeDeliveryPromo", language)}{" "}
                    🎁 🎁
                  </p>
                </div>
              ) : null}
            </div>
            <div className="bg-white px-4 md:mt-5">
                            <div className="flex items-center justify-between rounded-md bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border border-gray-200 px-4 py-2 mb-3 shadow-sm">
                              <div className="font-bold text-base md:text-lg bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent">
                                {t("orderList", language)}
                              </div>

                              {/* Optional Badge */}
                              <span
                                className={`text-xs font-medium ${isDark ? "text-white bg-gray-100" : "text-indigo-600 bg-white"} px-2 py-1 rounded-full shadow-sm`}
                              >
                                {products.length}
                              </span>
                            </div>{" "}
                            {products.map((item, index) => (
                              <div
                                key={index}
                                className="flex flex-col md:grid md:grid-cols-6 gap-0 md:gap-4 items-center mt-2 md:py-4 py-1 border-b"
                              >
                                {/* Product Info */}
                                <div className="flex gap-3 items-center col-span-3 w-full">
                                  <img
                                    src={assetUrl(item.selectedVariant.images[0].thumb)}
                                    alt={item.selectedVariant?.title}
                                    className="w-16 h-16 sm:w-20 sm:h-20 object-top object-cover rounded"
                                  />
                                  <div className="flex-1 min-w-0 px-1">
                                    <div className="flex justify-between">
                                      <div className="min-w-0">
                                        <Link
                                          to={`/details/${item.catalogue}/${item.item_id}`}
                                          className="text-sm hidden md:flex truncate"
                                        >
                                          {item.selectedVariant?.title}
                                        </Link>
                                        <Link
                                          to={`/details/${item.catalogue}/${item.item_id}`}
                                        >
                                          <p
                                            className={`text-xs sm:text-sm ${isDark ? "text-white" : "text-gray-900"} mt-1 md:hidden truncate font-bold`}
                                          >
                                            {item.selectedVariant?.title}
                                          </p>
                                        </Link>
                                        {/* <p className="text-xs sm:text-sm text-gray-900 mt-1 md:hidden truncate font-bold">
                                            {item.selectedVariant?.title}
                                          </p> */}
                                      </div>
                                      <div className="shrink-0 sm:hidden">
                                        <button
                                          className="text-red-600 text-xs sm:text-sm"
                                          onClick={() => removeItem(index)}
                                        >
                                          <HiOutlineX className="w-4 h-4 mr-1" />
                                        </button>
                                      </div>
                                    </div>
                                    <div className={`text-xs text-gray-600`}>
                                      <h1> {item.catalogue_title}</h1>
                                    </div>
                                    <div className="flex justify-between">
                                      <div
                                        className={`text-xs flex font-semibold items-center ${isDark ? "text-white" : " text-backgroundColor"}`}
                                      >
                                        {(
                                          (item.selectedVariant?.flashPrice || 0) *
                                          (item.quantity || 1)
                                        ).toFixed(0)}
                                        <TbCurrencyTaka />
                                      </div>
                                      <div className="flex items-center">
                                        <button
                                          className=" w-6 h-6 rounded disabled:opacity-50 bg-gray-200"
                                          onClick={() => decreaseQuantity(index)}
                                          disabled={(item.quantity || 1) <= 1}
                                        >
                                          -
                                        </button>
                                        <span className="w-6 text-center text-xs">
                                          {item.quantity || 1}
                                        </span>
                                        <button
                                          className="w-6 h-6 text-white bg-darkBgColor rounded"
                                          onClick={() => increaseQuantity(index)}
                                        >
                                          +
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Price - Desktop */}
                                <div
                                  className={`hidden md:flex justify-center items-center  ${isDark ? "text-white" : " text-backgroundColor"}`}
                                >
                                  <span className="text-sm">
                                    {t("TK", language)} {item.selectedVariant?.flashPrice}
                                  </span>
                                </div>

                                {/* Quantity Controls - Desktop */}
                                <div className="md:hidden w-full mt-2">
                                  <div className="flex justify-between items-center">
                                    <div></div>
                                  </div>
                                </div>

                                {/* Mobile View - Price and Quantity */}
                                <div className="flex items-center justify-center">
                                  <button
                                    className="text-red-600 text-xs sm:text-sm mt-1 hidden md:flex"
                                    onClick={() => removeItem(index)}
                                  >
                                    <HiOutlineTrash className="w-5 h-5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
          </>
        )}
      </div>
      <div
        className={`fixed bottom-14 left-0 w-full z-50 ${isDark ? "bg-gray-100 border-slate-500" : "bg-white border-slate-200"} border-t-2 rounded-t-2xl  px-5 py-2 sm:hidden`}
      >
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span
              className={`text-sm  ${isDark ? "text-gray-200" : "text-gray-500"} font-medium`}
            >
              {t("subtotal", language)} ({products.length}{" "}
              {t("items", language)})
            </span>
            <span
              className={` ${isDark ? "text-white" : "text-backgroundColor"}`}
            >
              {subtotal} {t("TK", language)}
            </span>
          </div>
          <div className="flex justify-between text-sm pb-1">
            <span
              className={`text-sm  ${isDark ? "text-gray-200" : "text-gray-500"} font-medium`}
            >
              {t("deliveryCharge", language)}
            </span>
            <span
              className={` ${isDark ? "text-white" : "text-backgroundColor"}`}
            >
              {finalDeliveryPrice} {t("TK", language)}
            </span>
          </div>
          <div className="font-semibold text-blue-600 border-dashed border-t border-gray-300 pt- flex justify-between text-md">
            <span
              className={`text-sm  ${isDark ? "text-b;ue-200" : "text-blue-500"} font-medium`}
            >
              {t("price", language)}
            </span>
            <span className="text-blue-600 font-semibold">
              {totalPrice} {t("TK", language)}
            </span>
          </div>
        </div>
        <button
          onClick={handleSubmit}
          className="w-full bg-darkBgColor text-white md:py-3 py-2 rounded-full mt-2 transition-colors lg:text-base font-semibold"
        >
          {t("submitOrder", language)}
        </button>
      </div>

      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default OrderDetails;
